import { calculateStockAmounts } from './stockCost';
import { isStockPack, getStockPackError } from './stockPack';
import { getStandardStockWeightError, isStandardGranelPurchase, resolveStandardStockQuantity } from './stockEntryGuidance';
import { getPurchaseFormatError, stockPurchaseFields } from './stockPurchaseFormat';

export const STOCK_UNIT_OPTIONS = ['gamela', 'balde', 'granel', 'bin', 'malla', 'cajonMadera', 'caja', 'saco', 'cajonNegro', 'cajonBlanco', 'kg', 'unidad', 'pack'];

// Match the keys used by the stock conversion API.
export const stockUnitKey = (value) => {
  const unit = String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (unit.includes('kg') || unit.includes('kilo')) return 'kg';
  return ['caja', 'cajon', 'saco', 'malla', 'unidad', 'balde', 'gamela', 'plancha', 'bandeja', 'bin', 'granel']
    .find((key) => unit.includes(key)) || unit;
};

export const getStockSaleUnits = (formats = []) => Array.from(new Map(formats
  .filter((format) => format.metricUnit)
  .map((format) => [stockUnitKey(format.metricUnit), { key: stockUnitKey(format.metricUnit), label: format.metricUnit }])).values());

export const getMissingStockConversions = ({ metricUnit, saleUnits = [], conversions = [] }) => {
  const fromKey = isStockPack(metricUnit) ? 'unidad' : stockUnitKey(metricUnit);
  if (!fromKey) return [];
  return saleUnits.filter((unit) => unit.key !== fromKey && !conversions.some((conversion) => (
    stockUnitKey(conversion.fromUnitKey || conversion.fromUnit) === fromKey
    && stockUnitKey(conversion.toUnitKey || conversion.toUnit) === unit.key
    && Number(conversion.factor) > 0
  )));
};

export const buildStockEntryPayload = ({ draft, listType, variety, isResaleOnly = false, missingConversions = [], conversionInputs = {} }) => {
  const isImportList = listType === 'importaciones';
  const purchaseMode = !isImportList ? draft.purchaseMode : null;
  if (purchaseMode !== undefined && purchaseMode !== null) {
    const formatError = getPurchaseFormatError(draft);
    if (formatError) throw new Error(formatError);
  }
  const metricUnit = String(draft.metricUnit || '').trim();
  const granel = isStandardGranelPurchase({ isImportList, metricUnit, purchaseMode });
  const positive = (value) => String(value ?? '').trim() !== '' && Number.isFinite(Number(value)) && Number(value) > 0;
  const optionalNumber = (value) => String(value ?? '').trim() === '' ? null : Number(value);
  if (!metricUnit) throw new Error('Selecciona la unidad de compra.');
  const packError = getStockPackError({ ...draft, purchaseMode });
  if (packError) throw new Error(packError);
  const quantity = Number(resolveStandardStockQuantity({ isImportList, metricUnit, purchaseMode, quantity: draft.quantity }));
  const price = Number(draft.price || 0);
  const priceWithoutIva = Number(draft.priceWithoutIva || 0);
  if (!positive(quantity)) throw new Error('La cantidad debe ser mayor a 0.');
  if (![price, priceWithoutIva].every((value) => Number.isSafeInteger(value) && value >= 0)
    || price + priceWithoutIva <= 0) throw new Error('Ingresa montos enteros no negativos y un total de compra mayor a 0.');
  const weightError = getStandardStockWeightError({ isImportList, metricUnit, purchaseMode, weight: draft.weight });
  if (weightError) throw new Error(weightError);
  for (const field of ['weight', ...(isImportList ? ['lengthCm', 'widthCm', 'heightCm'] : [])]) {
    const required = isImportList && !isResaleOnly;
    if ((required || String(draft[field] ?? '').trim() !== '') && !positive(draft[field])) {
      throw new Error(field === 'weight' ? 'Ingresa un peso mayor a 0 kg.' : 'Ingresa largo, ancho y alto mayores a 0 cm.');
    }
  }
  if (!purchaseMode && metricUnit.toLowerCase() === 'bin' && (!Number.isSafeInteger(Number(draft.bultosPerBin)) || Number(draft.bultosPerBin) <= 0)) {
    throw new Error('Ingresa los bultos por bin como un entero mayor a 0.');
  }
  if (isImportList && !draft.providerId) throw new Error('Selecciona un proveedor para la importación.');
  const conversions = missingConversions.map((unit) => {
    const input = conversionInputs[unit.key] || {};
    if (!positive(input.fromAmount ?? 1) || !positive(input.toAmount)) throw new Error(`Ingresa la conversión desde ${metricUnit} a ${unit.label}.`);
    return { varietyId: variety.id, fromUnit: isStockPack(metricUnit) ? 'unidad' : metricUnit, fromAmount: Number(input.fromAmount ?? 1), toUnit: unit.label, toAmount: Number(input.toAmount) };
  });
  const payload = {
    varietyId: variety.id, varietyName: variety.name || variety.varietyName,
    varietyImage: variety.image || variety.varietyImage || null,
    price, priceWithoutIva, quantity, metricUnit, weight: optionalNumber(draft.weight),
    providerId: draft.providerId || undefined,
    ...(!purchaseMode && isStockPack(metricUnit) ? { unitsPerPack: Number(draft.unitsPerPack) } : {}),
    ...(!purchaseMode && metricUnit.toLowerCase() === 'bin' ? { bultosPerBin: Number(draft.bultosPerBin) } : {}),
    ...(granel ? { weightKg: Number(draft.weight) } : {}),
    ...(purchaseMode ? stockPurchaseFields(draft) : {}),
    ...(isImportList ? {
      weightKg: optionalNumber(draft.weight), lengthCm: optionalNumber(draft.lengthCm),
      widthCm: optionalNumber(draft.widthCm), heightCm: optionalNumber(draft.heightCm), conversions,
    } : {}),
  };
  if (!Number.isFinite(calculateStockAmounts(payload).stockCostAmount)) throw new Error('El total de compra es demasiado grande.');
  return payload;
};
